import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, Languages } from "lucide-react";

import { Button } from "@shared/ui/button";

import { localizedHref, resolveLang } from "../lib/links";
import { useEcosystemCategories } from "../ecosystem-section/featureCategories";
import { HeroVisual } from "./HeroVisual";

const ASSURANCES = ["free", "private", "open", "selfHostable"] as const;

export function HeroSection() {
  const { t, i18n } = useTranslation("landing");
  const lang = resolveLang(i18n.resolvedLanguage);
  const { featureCount, categoryCount } = useEcosystemCategories();

  return (
    <section className="relative overflow-hidden bg-background pt-24 sm:pt-28">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 flex h-[36rem] items-start justify-center"
        aria-hidden="true"
      >
        <div className="h-[28rem] w-[60rem] max-w-full rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-10 sm:px-6 lg:grid-cols-[1.02fr_0.98fr] lg:px-8 lg:pb-28 lg:pt-16">
        <div className="relative z-10 max-w-3xl">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.32em] text-primary/80">
            {t("hero.eyebrow", {
              count: featureCount,
              categories: categoryCount,
            })}
          </p>
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground shadow-sm">
            <Languages className="h-4 w-4 text-primary" aria-hidden="true" />
            {t("hero.badge")}
          </div>

          <h1 className="font-serif text-5xl font-semibold leading-[1.05] tracking-[-0.05em] text-foreground sm:text-6xl sm:tracking-[-0.06em] lg:text-7xl xl:text-[5.25rem]">
            {t("hero.headline.lineOne")}
            <span className="block text-primary">{t("hero.headline.highlight")}</span>
            <span className="mt-2 block text-2xl font-medium leading-snug tracking-normal text-muted-foreground sm:text-3xl lg:text-4xl">
              {t("hero.headline.tagline")}
            </span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground sm:text-xl">
            {t("hero.subheadline")}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button size="lg" asChild>
              <a href={localizedHref("/register", lang)}>
                {t("hero.primaryCta")}
                <ArrowRight className="h-4 w-4 rtl:scale-x-[-1]" aria-hidden="true" />
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="#ecosystem">{t("hero.secondaryCta")}</a>
            </Button>
          </div>

          <ul className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
            {ASSURANCES.map((item) => (
              <li key={item} className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                {t(`hero.assurances.${item}`)}
              </li>
            ))}
          </ul>
        </div>

        <HeroVisual />
      </div>
    </section>
  );
}

export default HeroSection;
